import { useEffect, useRef, useState } from 'react'
import { Outlet, useNavigate } from 'react-router-dom'
import { CheckCircleIcon } from '@solar-icons/react/linear/check-circle'
import { HamburgerMenuIcon } from '@solar-icons/react/linear/hamburger-menu'
import { ShareIcon } from '@solar-icons/react/linear/share'
import { MenuDotsIcon } from '@solar-icons/react/bold/menu-dots'
import { FileIcon } from '@solar-icons/react/linear/file'
import { PinIcon } from '@solar-icons/react/linear/pin'
import { ArchiveIcon } from '@solar-icons/react/linear/archive'
import { TrashBinTrashIcon } from '@solar-icons/react/linear/trash-bin-trash'
import { FolderIcon } from '@solar-icons/react/linear/folder'
import { AltArrowRightIcon } from '@solar-icons/react/linear/alt-arrow-right'
import { Sidebar } from './Sidebar'
import { Button } from '@/components/ui'
import { cn } from '@/lib/utils/cn'
import { useSessionStore } from '@/stores/sessionStore'
import { useChatsStore } from '@/stores/chatsStore'
import { copyText } from '@/lib/utils/clipboard'

/**
 * Application chrome: persistent sidebar, a topbar that names the current view,
 * and a scroll container the views fill. Views own their own scrolling so the
 * chat thread can pin to the bottom independently of the page.
 */
export function AppShell() {
  // The drawer closes itself from the nav links and the backdrop; there is no
  // navigation path into a view that leaves it open.
  const [navOpen, setNavOpen] = useState(false)
  const [shared, setShared] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [deleteArmed, setDeleteArmed] = useState(false)
  const shareTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  const navigate = useNavigate()
  const newSession = useSessionStore((state) => state.newSession)
  const conversationId = useSessionStore((state) => state.conversationId)
  const hasExchanges = useSessionStore((state) => state.exchanges.length > 0)
  const chats = useChatsStore((state) => state.chats)
  const togglePin = useChatsStore((state) => state.togglePin)
  const removeChat = useChatsStore((state) => state.remove)

  const currentChat = chats.find((chat) => chat.id === conversationId)
  const isPinned = currentChat?.pinned ?? false

  useEffect(() => () => {
    if (shareTimerRef.current) clearTimeout(shareTimerRef.current)
  }, [])

  const closeMenu = () => {
    setMenuOpen(false)
    setDeleteArmed(false)
  }

  useEffect(() => {
    if (!menuOpen) return

    const onPointerDown = (event: PointerEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) closeMenu()
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeMenu()
    }

    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [menuOpen])

  const handleShare = async () => {
    const ok = await copyText(window.location.href)
    if (!ok) return
    setShared(true)
    if (shareTimerRef.current) clearTimeout(shareTimerRef.current)
    shareTimerRef.current = setTimeout(() => setShared(false), 1600)
  }

  const handleTogglePin = () => {
    togglePin(conversationId)
    closeMenu()
  }

  const handleDelete = () => {
    if (!deleteArmed) {
      setDeleteArmed(true)
      return
    }
    removeChat(conversationId)
    newSession()
    navigate('/')
    closeMenu()
  }

  return (
    <div className="flex h-full overflow-hidden bg-canvas">
      <Sidebar open={navOpen} onClose={() => setNavOpen(false)} />

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-14 shrink-0 items-center gap-3 px-4">
          <button
            type="button"
            onClick={() => setNavOpen(true)}
            aria-label="Open navigation"
            className="rounded-full p-1.5 text-muted hover:bg-hover hover:text-fg lg:hidden"
          >
            <HamburgerMenuIcon size={19} strokeWidth={1.75} />
          </button>

          <div className="flex min-w-0 items-center gap-2">
            <h1 className="truncate text-sm font-semibold text-fg">QAP</h1>
          </div>

          <div className="ml-auto flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => void handleShare()}
              title="Copy a link to this page"
              icon={
                shared ? (
                  <CheckCircleIcon size={14} strokeWidth={2} />
                ) : (
                  <ShareIcon size={14} strokeWidth={1.75} />
                )
              }
              className="hidden sm:inline-flex"
            >
              {shared ? 'Copied' : 'Share'}
            </Button>

            <div ref={menuRef} className="relative">
              <button
                type="button"
                onClick={() => (menuOpen ? closeMenu() : setMenuOpen(true))}
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                aria-label="Chat options"
                title="Chat options"
                className="flex h-8 w-8 items-center justify-center rounded-full border border-line bg-surface text-muted transition-colors hover:bg-hover hover:text-fg"
              >
                <MenuDotsIcon size={18} className="rotate-90" />
              </button>

              {menuOpen && (
                <div
                  role="menu"
                  className="animate-rise absolute top-full right-0 z-20 mt-1.5 w-56 overflow-hidden rounded-xl border border-line bg-surface py-1.5 shadow-lg"
                >
                  <button
                    type="button"
                    role="menuitem"
                    onClick={closeMenu}
                    title="Coming soon"
                    className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-xs text-fg transition-colors hover:bg-hover"
                  >
                    <FileIcon size={15} strokeWidth={1.75} />
                    View files in chat
                  </button>

                  <button
                    type="button"
                    role="menuitem"
                    onClick={handleTogglePin}
                    disabled={!hasExchanges}
                    className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-xs text-fg transition-colors hover:bg-hover disabled:cursor-not-allowed disabled:opacity-45 disabled:hover:bg-transparent"
                  >
                    <PinIcon size={15} strokeWidth={1.75} />
                    {isPinned ? 'Unpin chat' : 'Pin chat'}
                  </button>

                  <button
                    type="button"
                    role="menuitem"
                    onClick={closeMenu}
                    title="Coming soon"
                    className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-xs text-fg transition-colors hover:bg-hover"
                  >
                    <ArchiveIcon size={15} strokeWidth={1.75} />
                    Archive
                  </button>

                  <div className="my-1.5 h-px bg-line" />

                  <button
                    type="button"
                    role="menuitem"
                    onClick={handleDelete}
                    onBlur={() => setDeleteArmed(false)}
                    disabled={!hasExchanges}
                    className={cn(
                      'flex w-full items-center gap-2.5 px-3 py-2 text-left text-xs transition-colors disabled:cursor-not-allowed disabled:opacity-45 disabled:hover:bg-transparent',
                      deleteArmed ? 'bg-danger-soft text-danger' : 'text-danger hover:bg-danger-soft',
                    )}
                  >
                    <TrashBinTrashIcon size={15} strokeWidth={1.75} />
                    {deleteArmed ? 'Click again to delete' : 'Delete'}
                  </button>

                  <div className="my-1.5 h-px bg-line" />

                  <button
                    type="button"
                    role="menuitem"
                    onClick={closeMenu}
                    title="Coming soon"
                    className="flex w-full items-center justify-between gap-2.5 px-3 py-2 text-left text-xs text-fg transition-colors hover:bg-hover"
                  >
                    <span className="flex items-center gap-2.5">
                      <FolderIcon size={15} strokeWidth={1.75} />
                      Move to project
                    </span>
                    <AltArrowRightIcon size={13} strokeWidth={1.75} />
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <main className="min-h-0 flex-1 overflow-hidden">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
